import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Filter, X, Folder, FolderOpen, Download, Info, ChevronLeft, ChevronRight, ChevronDown, CalendarRange,
  RefreshCw, ReceiptText, Loader2, AlertTriangle, ArrowUpDown, ArrowUp, ArrowDown,
} from "lucide-react";
import api, { erroMsg } from "./api";
import {
  MESES, MESES_CURTOS, TIPOS, TIPOS_DESC, trunc, isoLocal, cnpjFmt, fmtBR, btn, inputCls, Tip,
  IconeMoeda, Buscando, Alerta, Overlay, ModalDetalhes, RangeCalendar, Toolbar, Paginacao, useBusca, Cabecalho,
} from "./ui";

const POR_PAGINA = 50;
const ROTA_BASE = "/app/dashboard-federal";
const ROTA_FILTRO = `${ROTA_BASE}/comprovantes/filtro`;

export default function ComprovantesPage() {
  const navigate = useNavigate();
  const hoje = new Date();
  const [ano, setAno] = useState(hoje.getFullYear());
  const [mes, setMes] = useState(hoje.getMonth() + 1);
  const [modoAno, setModoAno] = useState(false);
  const [linhas, setLinhas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [busca, setBusca] = useState("");
  const [pagina, setPagina] = useState(1);
  const [ordem, setOrdem] = useState({ key: "razao_social", asc: true });
  const [visiveis, setVisiveis] = useState({});
  const [buscando, setBuscando] = useState(null);
  const [alerta, setAlerta] = useState(null);
  const [detalhe, setDetalhe] = useState(null);
  const [modalLote, setModalLote] = useState(false);
  const [modalBaixar, setModalBaixar] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const { data } = await api.get("/grade", { params: { ano, mes } });
      setLinhas(data);
    } catch (e) {
      setAlerta({ titulo: "Não foi possível carregar as empresas", msg: erroMsg(e) });
    } finally {
      setCarregando(false);
    }
  }, [ano, mes]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => setPagina(1), [busca, ano, mes]);

  const navMes = (delta) => {
    if (modoAno) { setAno(ano + delta); return; }
    const d = new Date(ano, mes - 1 + delta, 1);
    setAno(d.getFullYear());
    setMes(d.getMonth() + 1);
  };
  const noFuturo = (a, m) => a > hoje.getFullYear() || (a === hoje.getFullYear() && m > hoje.getMonth() + 1);

  const atualizar = async (l) => {
    setBuscando(`Consultando pagamentos de ${trunc(l.razao_social, 30)}.\nIsso pode levar alguns segundos...`);
    try {
      const { data } = await api.post("/atualizar", { cnpj: l.cnpj, ano, mes });
      await carregar();
      if (!data?.encontrados) {
        setAlerta({ icone: "info", titulo: "Consulta concluída", msg: `Nenhum pagamento encontrado para ${MESES[mes - 1]}/${ano}.` });
      }
    } catch (e) {
      setAlerta({ titulo: "Não foi possível consultar", msg: erroMsg(e) });
    } finally {
      setBuscando(null);
    }
  };

  const resumo = useMemo(() => {
    const r = { empresas: 0, comErro: 0 };
    TIPOS.forEach((t) => { r[t] = 0; });
    linhas.forEach((l) => {
      let tem = false;
      TIPOS.forEach((t) => { r[t] += l[t] || 0; if (l[t]) tem = true; });
      if (tem) r.empresas += 1;
      if (l.erro) r.comErro += 1;
    });
    return r;
  }, [linhas]);

  const colunas = [
    { key: "razao_social", label: "Empresa", fixa: true },
    { key: "cnpj", label: "CNPJ", exportar: (l) => cnpjFmt(l.cnpj) },
    ...TIPOS.map((t) => ({ key: t, label: t })),
    { key: "atualizar", label: "Atualizar", semExport: true },
  ];
  const vis = (k) => visiveis[k] !== false;

  const filtradas = useBusca(linhas, ["razao_social", "cnpj"], busca);
  const ordenadas = [...filtradas].sort((a, b) => {
    const k = ordem.key;
    const r = TIPOS.includes(k)
      ? (a[k] || 0) - (b[k] || 0) || a.razao_social.localeCompare(b.razao_social)
      : String(a[k] || "").localeCompare(String(b[k] || ""));
    return ordem.asc ? r : -r;
  });
  const ordenar = (key) => setOrdem((o) => (o.key === key
    ? { key, asc: !o.asc }
    : { key, asc: !TIPOS.includes(key) }));
  const Ord = ({ k, label, align = "left" }) => {
    const ativo = ordem.key === k;
    const Icone = !ativo ? ArrowUpDown : ordem.asc ? ArrowUp : ArrowDown;
    return (
      <button onClick={() => ordenar(k)}
        className={`inline-flex items-center gap-1 uppercase hover:text-foreground ${ativo ? "text-foreground" : ""} ${align === "center" ? "justify-center" : ""}`}>
        {label} <Icone className={`h-3 w-3 ${ativo ? "text-primary" : "opacity-60"}`} />
      </button>
    );
  };
  const paginaLinhas = ordenadas.slice((pagina - 1) * POR_PAGINA, pagina * POR_PAGINA);
  const nCols = colunas.filter((c) => vis(c.key)).length;

  return (
    <div className="space-y-6">
      <Cabecalho
        titulo="Comprovantes de Pagamento"
        trilha={[{ label: "Dashboard Federal", onClick: () => navigate(ROTA_BASE) }, { label: "Comprovantes de Pagamento" }]}
      >
        <button onClick={() => navigate(ROTA_FILTRO)} className={btn.outline}><Filter className="h-4 w-4" /> Filtro avançado</button>
        <button onClick={() => setModalBaixar(true)} className={btn.outline}><Download className="h-4 w-4" /> Baixar em lote</button>
        <button onClick={() => setModalLote(true)} className={btn.primary}><RefreshCw className="h-4 w-4" /> Buscar em lote</button>
      </Cabecalho>

      {/* Resumo do mês */}
      <section className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        {TIPOS.map((t) => (
          <div key={t} className="rounded-2xl border border-border bg-card p-4">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{t}</span>
              <ReceiptText className="h-4 w-4 text-primary/60" strokeWidth={1.8} />
            </div>
            <p className="mt-2 font-display text-2xl tabular-nums text-foreground">{carregando ? "—" : resumo[t]}</p>
            <p className="mt-0.5 text-[11px] text-muted-foreground">{TIPOS_DESC[t]}</p>
          </div>
        ))}
        <div className="col-span-2 rounded-2xl border border-border bg-card p-4 lg:col-span-1">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Empresas com pagamento</span>
          </div>
          <p className="mt-2 font-display text-2xl tabular-nums text-foreground">
            {carregando ? "—" : resumo.empresas}<span className="text-sm text-muted-foreground"> / {linhas.length}</span>
          </p>
          {resumo.comErro > 0
            ? <p className="mt-0.5 flex items-center gap-1 text-[11px] text-amber-600"><AlertTriangle className="h-3 w-3" /> {resumo.comErro} com falha na última consulta</p>
            : <p className="mt-0.5 text-[11px] text-muted-foreground">no mês selecionado</p>}
        </div>
      </section>

      {/* Tabela */}
      <section className="rounded-2xl border border-border bg-card">
        <div className="flex justify-center border-b border-border px-5 py-4">
          <div className="inline-flex items-center rounded-xl border border-border bg-card p-1 shadow-sm">
            <button onClick={() => navMes(-1)} className="flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"><ChevronLeft className="h-4 w-4" /></button>
            <button onClick={() => setModoAno(!modoAno)} className="flex h-9 min-w-[200px] items-center justify-center gap-2 rounded-lg px-4 text-sm font-semibold text-foreground hover:bg-muted">
              <CalendarRange className="h-4 w-4 text-primary" />
              {modoAno ? ano : `${MESES[mes - 1]} ${ano}`}
              <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition ${modoAno ? "rotate-180" : ""}`} />
            </button>
            <button onClick={() => navMes(1)} disabled={!modoAno && noFuturo(mes === 12 ? ano + 1 : ano, mes === 12 ? 1 : mes + 1)} className="flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-30"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>

        {modoAno && (
          <div className="grid grid-cols-4 gap-2 border-b border-border bg-muted/30 p-4 sm:grid-cols-6 lg:grid-cols-12">
            {MESES_CURTOS.map((m, i) => {
              const futuro = noFuturo(ano, i + 1);
              const atual = i + 1 === mes;
              return (
                <button key={m} disabled={futuro} onClick={() => { setMes(i + 1); setModoAno(false); }}
                  className={`h-9 rounded-lg text-xs font-medium transition ${futuro ? "text-muted-foreground/40" : atual ? "bg-primary text-primary-foreground" : "border border-border bg-card text-foreground hover:border-primary/40 hover:text-primary"}`}>
                  {m}
                </button>
              );
            })}
          </div>
        )}

        <div className="border-b border-border px-5 py-3">
          <Toolbar colunas={colunas} visiveis={visiveis} setVisiveis={setVisiveis} linhas={ordenadas}
            nome={`comprovantes_${String(mes).padStart(2, "0")}_${ano}`} busca={busca} setBusca={setBusca} />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {vis("razao_social") && (
                  <th className="px-5 py-3 text-left">
                    <Ord k="razao_social" label="Empresa" />
                  </th>
                )}
                {vis("cnpj") && <th className="px-3 py-3 text-left"><Ord k="cnpj" label="CNPJ" /></th>}
                {TIPOS.map((t) => vis(t) && <th key={t} className="w-24 px-2 py-3 text-center"><Ord k={t} label={t} align="center" /></th>)}
                {vis("atualizar") && <th className="w-24 px-5 py-3 text-right">Atualizar</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {carregando && (
                <tr><td colSpan={nCols} className="py-14 text-center text-sm text-muted-foreground">
                  <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Carregando empresas...</span>
                </td></tr>
              )}
              {!carregando && paginaLinhas.length === 0 && (
                <tr><td colSpan={nCols} className="py-14 text-center text-sm text-muted-foreground">Nenhuma empresa encontrada.</td></tr>
              )}
              {!carregando && paginaLinhas.map((l) => (
                <tr key={l.cnpj} className="transition-colors hover:bg-muted/30">
                  {vis("razao_social") && (
                    <td className="px-5 py-3">
                      <p className="font-medium text-foreground" title={l.razao_social}>{trunc(l.razao_social)}</p>
                    </td>
                  )}
                  {vis("cnpj") && <td className="px-3 py-3 font-mono text-xs text-muted-foreground">{cnpjFmt(l.cnpj)}</td>}
                  {TIPOS.map((t) => vis(t) && (
                    <td key={t} className="px-2 py-3 text-center">
                      <IconeMoeda tipo={t} qtd={l[t]} onClick={() => setDetalhe({ empresa: l, tipo: t })} />
                    </td>
                  ))}
                  {vis("atualizar") && (
                    <td className="px-5 py-3 text-right">
                      <Tip text={l.erro ? `Última consulta falhou:\n${l.erro}` : "Consultar pagamentos na Receita"}>
                        <button onClick={() => atualizar(l)} className={`${btn.icon} ${l.erro ? "text-amber-600 hover:text-amber-700" : ""}`}>
                          {l.erro ? <AlertTriangle className="h-4 w-4" /> : <RefreshCw className="h-4 w-4" />}
                        </button>
                      </Tip>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Paginacao pagina={pagina} setPagina={setPagina} total={ordenadas.length} porPagina={POR_PAGINA} />
      </section>

      {detalhe && (
        <ModalDetalhes empresa={detalhe.empresa} tipo={detalhe.tipo} ano={ano} mes={mes}
          onClose={() => setDetalhe(null)} setBuscando={setBuscando} setAlerta={setAlerta} />
      )}
      {modalLote && <ModalBuscarLote onClose={() => setModalLote(false)} setAlerta={setAlerta} total={linhas.length} />}
      {modalBaixar && <ModalBaixarLote ano={ano} mes={mes} onClose={() => setModalBaixar(false)} setAlerta={setAlerta} />}
      {buscando && <Buscando msg={buscando} />}
      {alerta && <Alerta {...alerta} onOk={() => setAlerta(null)} />}
    </div>
  );
}

/* ---------------- Buscar em lote ---------------- */
function ModalBuscarLote({ onClose, setAlerta, total }) {
  const [range, setRange] = useState(null);
  const [cal, setCal] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const buscar = async () => {
    if (!range) return;
    setEnviando(true);
    try {
      await api.post("/lote", { data_inicio: isoLocal(range[0]), data_fim: isoLocal(range[1]) });
      onClose();
      setAlerta({
        icone: "info",
        titulo: "Em processamento",
        msg: "Estamos consultando os pagamentos de todas as empresas. Isso pode levar alguns minutos — os comprovantes vão aparecendo na tela conforme ficam disponíveis.",
      });
    } catch (e) {
      setAlerta({ titulo: "Não foi possível iniciar a busca", msg: erroMsg(e) });
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Overlay onClose={onClose} z="z-[9990]">
      <div className="w-full max-w-lg rounded-2xl border border-border bg-card shadow-xl">
        <div className="flex items-start justify-between border-b border-border p-5">
          <div>
            <h3 className="text-base font-semibold text-foreground">Buscar comprovantes em lote</h3>
            <p className="mt-0.5 text-xs text-muted-foreground">Consulta os pagamentos das {total} empresas no período escolhido.</p>
          </div>
          <button onClick={onClose} className={btn.icon}><X className="h-4 w-4" /></button>
        </div>
        <div className="relative p-5">
          <label className="text-xs font-medium text-foreground">Período de pagamento <span className="text-destructive">*</span></label>
          <button onClick={() => setCal(!cal)} className={`${inputCls} mt-1.5 flex items-center gap-2 text-left`}>
            <CalendarRange className="h-4 w-4 text-muted-foreground" />
            {range ? `${fmtBR(range[0])} até ${fmtBR(range[1])}` : <span className="text-muted-foreground">Escolher datas</span>}
          </button>
          {cal && (
            <div className="absolute left-5 top-[88px] z-10">
              <RangeCalendar valor={range} onCancelar={() => setCal(false)} onSelecionar={(r) => { setRange(r); setCal(false); }} />
            </div>
          )}
          <div className="mt-4 flex items-start gap-2 rounded-xl bg-muted/50 p-3 text-xs leading-5 text-muted-foreground">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            Só consulta a lista de pagamentos. Os PDFs são gerados um a um, quando você clicar em “Gerar comprovante”.
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-border p-4">
          <button onClick={onClose} className={btn.ghost}>Cancelar</button>
          <button disabled={!range || enviando} onClick={buscar} className={btn.primary}>
            {enviando ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Buscar
          </button>
        </div>
      </div>
    </Overlay>
  );
}

/* ---------------- Baixar em lote ---------------- */
function ModalBaixarLote({ ano, mes, onClose, setAlerta }) {
  const [resumo, setResumo] = useState(null);
  const [modo, setModo] = useState("separadas");
  const [baixando, setBaixando] = useState(false);

  useEffect(() => {
    api.get("/download-lote/resumo", { params: { ano, mes } }).then((r) => setResumo(r.data)).catch(() => setResumo({ gerados: 0, total: 0 }));
  }, [ano, mes]);

  const baixar = async () => {
    setBaixando(true);
    try {
      const r = await api.get("/download-lote/arquivo", { params: { ano, mes, modo }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `comprovantes_${ano}${String(mes).padStart(2, "0")}.zip`;
      a.click();
      onClose();
    } catch (e) {
      let msg = erroMsg(e);
      if (e?.response?.data instanceof Blob) {
        try { msg = JSON.parse(await e.response.data.text()).detail; } catch (_) { /* noop */ }
      }
      setAlerta({ titulo: "Não foi possível baixar", msg });
    } finally {
      setBaixando(false);
    }
  };

  const pct = resumo?.total ? Math.round((resumo.gerados / resumo.total) * 100) : 0;
  const Opcao = ({ valor, titulo, texto, Icone }) => {
    const ativo = modo === valor;
    return (
      <button type="button" onClick={() => setModo(valor)}
        className={`flex w-full items-start gap-3 rounded-xl border p-3.5 text-left transition ${ativo ? "border-primary/50 bg-primary/5 ring-1 ring-primary/20" : "border-border hover:border-primary/30"}`}>
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${ativo ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}><Icone className="h-4 w-4" /></span>
        <span className="flex-1">
          <span className="block text-sm font-semibold text-foreground">{titulo}</span>
          <span className="mt-0.5 block text-xs leading-5 text-muted-foreground">{texto}</span>
        </span>
        <span className={`mt-1 flex h-4 w-4 items-center justify-center rounded-full border ${ativo ? "border-primary" : "border-border"}`}>
          {ativo && <span className="h-2 w-2 rounded-full bg-primary" />}
        </span>
      </button>
    );
  };

  return (
    <Overlay onClose={onClose} z="z-[9990]">
      <div className="w-full max-w-lg rounded-2xl border border-border bg-card shadow-xl">
        <div className="flex items-start justify-between border-b border-border p-5">
          <div>
            <h3 className="text-base font-semibold text-foreground">Baixar comprovantes gerados</h3>
            <p className="mt-0.5 text-xs text-muted-foreground">{MESES[mes - 1]} de {ano}</p>
          </div>
          <button onClick={onClose} className={btn.icon}><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-5 p-5">
          <div className="rounded-xl border border-border p-4">
            <div className="flex items-end justify-between">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Gerados no período</span>
              <span className="font-display text-xl tabular-nums text-foreground">{resumo ? `${resumo.gerados}/${resumo.total}` : "…"}</span>
            </div>
            <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-muted">
              <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} />
            </div>
            <p className="mt-2 text-[11px] text-muted-foreground">Só os comprovantes já gerados entram no arquivo.</p>
          </div>
          <div className="space-y-2">
            <p className="text-xs font-medium text-foreground">Como organizar o arquivo</p>
            <Opcao valor="separadas" Icone={Folder} titulo="Uma pasta por empresa"
              texto="Arquivo .zip com uma pasta para cada CNPJ, contendo os comprovantes dela." />
            <Opcao valor="unica" Icone={FolderOpen} titulo="Tudo em uma pasta"
              texto="Arquivo .zip com todos os comprovantes juntos, identificados pelo CNPJ no nome." />
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-border p-4">
          <button onClick={onClose} className={btn.ghost}>Cancelar</button>
          <button disabled={baixando || !resumo?.gerados} onClick={baixar} className={btn.primary}>
            {baixando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Baixar .zip
          </button>
        </div>
      </div>
    </Overlay>
  );
}
